import { useEffect } from 'react';
import clsx from 'clsx';

import ProductCardV2 from 'features/shop/ProductCardV2';
import DrawerModal from 'shared/DrawerModal';
import { trackEvent } from 'utils/analyticsUtils';

const BACK_IN_STOCK_WAITLIST_SUCCESS_MODAL_ID = 'back-in-stock-waitlist-success-modal';
const BACK_IN_STOCK_WAITLIST_SUCCESS_MODAL_NAME = 'Back in stock waitlist success';
const BACK_IN_STOCK_WAITLIST_SUCCESS_MODAL_TYPE = 'BACK_IN_STOCK_WAITLIST_SUCCESS_MODAL';

const PRODUCT_CARD_WIDTH_CLASSNAME = 'w-[145px] md:w-[200px]';

const MODAL_WIDTH_BY_COLUMN_COUNT = {
    1: 'md:w-[495px] md:max-w-[495px] lg:max-w-[495px]',
    2: 'md:w-[495px] md:max-w-[495px] lg:max-w-[495px]',
    3: 'md:w-[495px] md:max-w-[495px] lg:w-[695px] lg:max-w-[695px]',
    4: 'md:w-[495px] md:max-w-[495px] lg:w-[925px] lg:max-w-[925px]',
};

const DESKTOP_GRID_COLS_BY_COLUMN_COUNT = {
    1: 'md:grid-cols-1',
    2: 'md:grid-cols-2',
    3: 'lg:grid-cols-3',
    4: 'lg:grid-cols-4',
};

/**
 * @param {number} productCount
 * @returns {number}
 */
function getDesktopColumnCount(productCount) {
    return Math.min(productCount, 4);
}

/**
 * @param {number} productCount
 * @returns {string}
 */
function getModalMaxWidthClassName(productCount) {
    return MODAL_WIDTH_BY_COLUMN_COUNT[getDesktopColumnCount(productCount)];
}

/**
 * @returns {string}
 */
function getModalContentClassName() {
    return 'm-[10px] rounded-md outline-none pt-15 md:m-0 md:grow-0 md:pt-15';
}

/**
 * @param {number} productCount
 * @returns {string}
 */
function getProductLayoutClassName(productCount) {
    const desktopColumnCount = getDesktopColumnCount(productCount);

    return clsx(
        'mx-auto grid w-fit gap-10 md:gap-15',
        productCount === 1 ? 'grid-cols-1 justify-items-center' : 'grid-cols-2 justify-items-start',
        DESKTOP_GRID_COLS_BY_COLUMN_COUNT[desktopColumnCount]
    );
}

/**
 * @returns {string}
 */
function getProductCardWidthClassName() {
    return PRODUCT_CARD_WIDTH_CLASSNAME;
}

/**
 * @param {number} index - Zero-based position in the fulfilled items list
 * @returns {Object} ProductCardV2 metadata for the back-in-stock waitlist success modal
 */
function getBackInStockWaitlistSuccessModalProductCardMeta(index) {
    return {
        carouselID: BACK_IN_STOCK_WAITLIST_SUCCESS_MODAL_ID,
        carouselName: BACK_IN_STOCK_WAITLIST_SUCCESS_MODAL_NAME,
        carouselPosition: index,
        carouselType: BACK_IN_STOCK_WAITLIST_SUCCESS_MODAL_TYPE,
        isCarouselModalCard: true,
    };
}

/**
 * Modal shown when waitlisted items have been automatically added to the cart.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {() => void} props.onClose - Callback when the modal is closed
 * @param {import('features/shop/ProductCardV2/types').ProductData[]} props.products - Fulfilled product data
 */
function BackInStockWaitlistSuccessModal({ onClose, open, products = [] }) {
    useEffect(() => {
        if (!open || products.length === 0) {
            return;
        }

        trackEvent('back in stock waitlist success modal opened');
    }, [open, products.length]);

    if (!open || products.length === 0) {
        return null;
    }

    const productCardWidthClassName = getProductCardWidthClassName();
    const headline = `Score! Your waitlisted ${products.length === 1 ? 'item has' : 'items have'} been added to your cart:`;

    const handleClose = () => {
        trackEvent('back in stock waitlist success modal closed');
        onClose();
    };

    return (
        <DrawerModal
            onClose={handleClose}
            drawerProps={{
                sx: { zIndex: 6000 },
            }}
            modalContentClassName={getModalContentClassName()}
            modalMaxWidthClassName={getModalMaxWidthClassName(products.length)}
            modalProps={{
                includeHorizontalPadding: false,
            }}
        >
            <h2 className="text-heading-xs mb-15 px-40 text-center md:mb-20 md:px-60">{headline}</h2>
            <div
                className="max-h-[calc(90vh-120px)] overflow-y-auto text-black"
                data-testid="back-in-stock-waitlist-success-product-scroll"
            >
                <div
                    className={getProductLayoutClassName(products.length)}
                    data-testid="back-in-stock-waitlist-success-product-layout"
                >
                    {products.map((product, index) => (
                        <div
                            key={product.id}
                            className={productCardWidthClassName}
                            data-testid={`back-in-stock-waitlist-success-product-card-${product.id}`}
                        >
                            <ProductCardV2
                                data={product}
                                layout="vertical"
                                metaData={getBackInStockWaitlistSuccessModalProductCardMeta(index)}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </DrawerModal>
    );
}

export default BackInStockWaitlistSuccessModal;
